'use client'

import { useMemo, useRef, useState } from 'react'

export type Pt = { date: string; rate: number }

/**
 * Interactive 12-month rate chart. Replaces a static sparkline that showed a shape but no numbers.
 *
 * Deliberately hand-rolled SVG rather than a charting library: the page is `force-dynamic` and ranks on
 * search, so shipping a ~100KB chart bundle to every visitor would cost more (LCP, and mobile users on
 * the "mortgage rates today" query are overwhelmingly on phones) than this earns.
 *
 * ⚠ POINTER, NOT MOUSE. `onPointerMove` covers touch as well, so the tooltip works on a phone where a
 * mouse-only handler would be dead. Touch is the majority of this page's traffic.
 * ⚠ The y-axis is PADDED around the data range, never anchored at zero: mortgage rates live in a narrow
 * band and a zero-based axis would flatten a 1.5-point swing into a straight line.
 */
export default function RateChart({ points, accent = '#10b981', label = '30-year fixed, past 12 months', unit = '%', decimals = 2 }: {
  points: Pt[]; accent?: string
  /** Headline above the chart. Defaults to the mortgage wording; pass your own for other series. */
  label?: string
  /** Suffix on every printed value ('%' for rates, '' for FX). */
  unit?: string
  /** FX needs 4 places where a mortgage rate needs 2, so precision is per-series, not global. */
  decimals?: number
}) {
  const [hover, setHover] = useState<number | null>(null)
  const wrapRef = useRef<HTMLDivElement>(null)

  const W = 720, H = 260, PL = 44, PR = 14, PT = 16, PB = 30
  const { xs, ys, lo, hi, minI, maxI } = useMemo(() => {
    const rs = points.map((p) => p.rate)
    const rawLo = Math.min(...rs), rawHi = Math.max(...rs)
    const pad = Math.max(0.08, (rawHi - rawLo) * 0.18)
    const lo = rawLo - pad, hi = rawHi + pad
    const xs = points.map((_, i) => PL + (i / Math.max(1, points.length - 1)) * (W - PL - PR))
    const ys = points.map((p) => PT + (1 - (p.rate - lo) / Math.max(1e-6, hi - lo)) * (H - PT - PB))
    return { xs, ys, lo, hi, minI: rs.indexOf(rawLo), maxI: rs.indexOf(rawHi) }
  }, [points])

  if (points.length < 2) return null

  const line = points.map((_, i) => `${i ? 'L' : 'M'}${xs[i].toFixed(1)},${ys[i].toFixed(1)}`).join(' ')
  const area = `${line} L${xs[xs.length - 1].toFixed(1)},${H - PB} L${xs[0].toFixed(1)},${H - PB} Z`
  const ticks = [hi, (hi + lo) / 2, lo]
  const fmtD = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
  const active = hover ?? points.length - 1
  const cur = points[active]

  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = wrapRef.current
    if (!el) return
    const box = el.getBoundingClientRect()
    // Map the pointer into viewBox space, since the SVG scales with the container width.
    const vx = ((e.clientX - box.left) / box.width) * W
    let best = 0, bd = Infinity
    for (let i = 0; i < xs.length; i++) { const d = Math.abs(xs[i] - vx); if (d < bd) { bd = d; best = i } }
    setHover(best)
  }

  return (
    <div>
      <div className="mb-2 flex items-baseline justify-between gap-3">
        <div className="text-[13px] font-semibold text-zinc-300">{label}</div>
        <div className="text-[12px] tabular-nums text-zinc-400">
          <span className="font-semibold text-zinc-100">{cur.rate.toFixed(decimals)}{unit}</span>
          <span className="ml-2 text-zinc-500">{fmtD(cur.date)}</span>
        </div>
      </div>

      <div
        ref={wrapRef}
        className="relative touch-pan-y select-none"
        onPointerMove={onMove}
        onPointerLeave={() => setHover(null)}
      >
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label={label}>
          <defs>
            <linearGradient id="mrFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={accent} stopOpacity="0.28" />
              <stop offset="100%" stopColor={accent} stopOpacity="0" />
            </linearGradient>
          </defs>

          {ticks.map((t, i) => {
            const y = PT + (1 - (t - lo) / Math.max(1e-6, hi - lo)) * (H - PT - PB)
            return (
              <g key={i}>
                <line x1={PL} y1={y} x2={W - PR} y2={y} stroke="#3f3f46" strokeOpacity="0.45" strokeDasharray="3 4" />
                <text x={PL - 8} y={y + 4} textAnchor="end" className="fill-zinc-500" style={{ fontSize: 11 }}>{t.toFixed(decimals)}{unit}</text>
              </g>
            )
          })}

          <path d={area} fill="url(#mrFill)" />
          <path d={line} fill="none" stroke={accent} strokeWidth="2.25" strokeLinejoin="round" strokeLinecap="round" />

          {/* 12-month extremes, labelled in place so the range is readable without a legend. */}
          {[{ i: maxI, t: 'high', dy: -9 }, { i: minI, t: 'low', dy: 16 }].map(({ i, t, dy }) => (
            <g key={t}>
              <circle cx={xs[i]} cy={ys[i]} r="3" fill="#a1a1aa" />
              <text x={Math.min(Math.max(xs[i], PL + 26), W - PR - 26)} y={ys[i] + dy} textAnchor="middle" className="fill-zinc-400" style={{ fontSize: 10.5 }}>
                {t} {points[i].rate.toFixed(decimals)}{unit}
              </text>
            </g>
          ))}

          <text x={PL} y={H - 8} className="fill-zinc-500" style={{ fontSize: 11 }}>{fmtD(points[0].date)}</text>
          <text x={W - PR} y={H - 8} textAnchor="end" className="fill-zinc-500" style={{ fontSize: 11 }}>{fmtD(points[points.length - 1].date)}</text>

          {hover != null && (
            <g>
              <line x1={xs[hover]} y1={PT} x2={xs[hover]} y2={H - PB} stroke={accent} strokeOpacity="0.5" strokeWidth="1" />
              <circle cx={xs[hover]} cy={ys[hover]} r="4.5" fill={accent} stroke="#09090b" strokeWidth="2" />
            </g>
          )}
          {hover == null && <circle cx={xs[xs.length - 1]} cy={ys[ys.length - 1]} r="4" fill={accent} stroke="#09090b" strokeWidth="2" />}
        </svg>
      </div>
      <div className="mt-1 text-center text-[11px] text-zinc-600 sm:hidden">Drag across the chart to read any week</div>
      <div className="mt-1 hidden text-center text-[11px] text-zinc-600 sm:block">Hover the chart to read any week</div>
    </div>
  )
}

/**
 * 52-week range bar — where today's rate sits between the 12-month low and high.
 *
 * The old page printed the min and max as two numbers, which tells a reader the range but not the one
 * thing they actually want: whether rates are currently cheap or expensive relative to the past year.
 * A position marker answers that at a glance.
 */
export function RangeBar({ lo, hi, now, accent = '#10b981', unit = '%', decimals = 2, lowLabel = 'low', highLabel = 'high' }: {
  lo: number; hi: number; now: number; accent?: string; unit?: string; decimals?: number
  /** FX reads better as "dollar weakest / strongest" than "low / high". */
  lowLabel?: string; highLabel?: string
}) {
  const span = Math.max(1e-6, hi - lo)
  const pct = Math.min(100, Math.max(0, ((now - lo) / span) * 100))
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between text-[12px]">
        <span className="font-semibold text-zinc-400">52-week range</span>
        <span className="tabular-nums text-zinc-500">
          {pct < 25 ? 'near the low end' : pct > 75 ? 'near the high end' : 'mid-range'}
        </span>
      </div>
      <div className="relative h-2.5 w-full rounded-full bg-gradient-to-r from-emerald-500/45 via-zinc-600/40 to-rose-500/45">
        <div
          className="absolute top-1/2 h-4 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-zinc-950"
          style={{ left: `${pct}%`, background: accent }}
          aria-hidden
        />
      </div>
      <div className="mt-1.5 flex items-baseline justify-between text-[12px] tabular-nums">
        <span className="text-zinc-400"><span className="font-semibold text-emerald-400">{lo.toFixed(decimals)}{unit}</span> {lowLabel}</span>
        <span className="font-semibold text-zinc-100">{now.toFixed(decimals)}{unit} now</span>
        <span className="text-zinc-400">{highLabel} <span className="font-semibold text-rose-400">{hi.toFixed(decimals)}{unit}</span></span>
      </div>
    </div>
  )
}
